import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const CART_KEY = "wc_cart_id";
const CartCtx = createContext(null);

function readStoredId() {
  try {
    return localStorage.getItem(CART_KEY) || "";
  } catch {
    return "";
  }
}

function storeId(id) {
  try {
    if (id) localStorage.setItem(CART_KEY, id);
    else localStorage.removeItem(CART_KEY);
  } catch {
    /* ignore */
  }
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const initRef = useRef(false);

  // Hydrate cart from localStorage on first mount
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    const id = readStoredId();
    if (!id) return;
    setLoading(true);
    api
      .get("/cart", { params: { cart_id: id } })
      .then((r) => setCart(r.data))
      .catch(() => {
        // Cart expired or invalid — wipe it
        storeId("");
      })
      .finally(() => setLoading(false));
  }, []);

  const persist = useCallback((data) => {
    setCart(data);
    if (data?.id) storeId(data.id);
  }, []);

  const addItem = useCallback(
    async ({ variantId, quantity = 1, attributes = null }) => {
      setLoading(true);
      try {
        const existing = cart?.id || readStoredId();
        let res;
        if (existing) {
          res = await api.post("/cart/add", {
            cart_id: existing,
            variant_id: String(variantId),
            quantity,
            attributes: attributes || undefined,
          });
        } else {
          res = await api.post("/cart", {
            variant_id: String(variantId),
            quantity,
            attributes: attributes || undefined,
          });
        }
        persist(res.data);
        setOpen(true);
        return res.data;
      } finally {
        setLoading(false);
      }
    },
    [cart, persist]
  );

  const updateLine = useCallback(
    async (lineId, quantity) => {
      if (!cart?.id) return;
      setLoading(true);
      try {
        const res = await api.patch("/cart/lines", {
          cart_id: cart.id,
          line_id: lineId,
          quantity,
        });
        persist(res.data);
      } finally {
        setLoading(false);
      }
    },
    [cart, persist]
  );

  const removeLine = useCallback(
    async (lineId) => {
      if (!cart?.id) return;
      setLoading(true);
      try {
        const res = await api.post("/cart/lines/remove", {
          cart_id: cart.id,
          line_id: lineId,
        });
        persist(res.data);
      } finally {
        setLoading(false);
      }
    },
    [cart, persist]
  );

  const value = useMemo(
    () => ({
      cart,
      count: cart?.total_quantity || 0,
      loading,
      open,
      openDrawer: () => setOpen(true),
      closeDrawer: () => setOpen(false),
      addItem,
      updateLine,
      removeLine,
    }),
    [cart, loading, open, addItem, updateLine, removeLine]
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
